import {
  Box,
  Button,
  Card,
  Stack,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";

export type LegendConfig = {
  direction: "column" | "row";
  position: "above" | "right" | "below" | "left";
  showExpandButton?: boolean;
  onExpandButtonClick?: () => void;
  expandButtonText?: string;
  expandButtonIcon?: React.ReactNode;
};

export type ChartLegendProps = {
  config?: LegendConfig;
  items: {
    color: string;
    value: string | React.ReactNode;
    tooltip?: string;
    type?: "swatch" | "dashed-line";
    isSelected?: boolean;
    key?: string;
  }[];
  onItemClick?: (index: number) => void;
  selectedIndex?: number | null;
  isSelectedValue?: (value: string) => boolean;
  sx?: SxProps;
};

export default function ChartLegend({
  config,
  items,
  onItemClick,
  selectedIndex,
  sx,
}: ChartLegendProps) {
  return (
    <Stack
      direction={config?.direction ?? "column"}
      justifyContent={config?.direction === "row" ? "left" : "center"}
      gap={1}
      sx={{ p: 1, ...sx }}
    >
      {items.map((i, index) => {
        const isSelected =
          i.isSelected ??
          (selectedIndex === null ||
            selectedIndex === undefined ||
            (Number.isInteger(selectedIndex) && index === selectedIndex));

        return (
          <Tooltip
            title={i.tooltip ?? i.value}
            key={i.key ?? `${i.color}-${i.value}`}
          >
            <Card
              sx={{
                border: "none",
                boxShadow: "none",
                cursor: onItemClick ? "pointer" : "default",
                zIndex: 1,
              }}
              onClick={() => onItemClick && onItemClick(index)}
            >
              <Stack
                direction="row"
                gap={1}
                alignItems="center"
                marginRight={config?.direction === "row" ? 2 : 0}
              >
                {(i.type ?? "swatch") === "swatch" && (
                  <Box
                    sx={{
                      width: "16px",
                      height: "16px",
                      minWidth: "16px",
                      borderRadius: "2px",
                      backgroundColor: i.color,
                      opacity: isSelected ? 1 : 0.3,
                    }}
                  />
                )}
                {i.type === "dashed-line" && (
                  <Box
                    sx={{
                      width: "16px",
                      height: "2px",
                      minWidth: "16px",
                      borderBottom: `1px dashed ${i.color}`,
                      opacity: isSelected ? 1 : 0.3,
                    }}
                  />
                )}
                {typeof i.value === "string" ? (
                  <Typography
                    variant="body2"
                    noWrap={true}
                    sx={{ opacity: isSelected ? 1 : 0.3 }}
                  >
                    {i.value}
                  </Typography>
                ) : (
                  i.value
                )}
              </Stack>
            </Card>
          </Tooltip>
        );
      })}
      {config?.showExpandButton && (
        <Box display="flex" justifyContent="start">
          <Button
            onClick={config?.onExpandButtonClick}
            variant="text"
            size="small"
            endIcon={config?.expandButtonIcon}
          >
            {config?.expandButtonText}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
